import { defineStore } from "pinia"

// The access token the google_token function returns (see README in common/google).
export interface GoogleToken {
  token: string
  expires_at: string
  /** The scopes this token was granted, space-separated. */
  scope?: string
}

// Narrow scopes Connected Apps get (GOOGLE_SCOPE_FOR in google_token's
// tokens.js) that the app's own scopes do not already list by name: Google
// refreshes a token only to scopes the grant lists by name.
export const CONNECTED_APP_SCOPES = [
  "https://www.googleapis.com/auth/calendar.events.readonly",
  "https://www.googleapis.com/auth/gmail.readonly",
]

// shared state for google APIs & libraries
export const useGoogleStore = defineStore("google", {
  state: () => ({
    // manual authorization needs to be run for the current user
    authorizationNeeded: false,
    // google identity services library is loaded
    librariesLoaded: false,
    // libraries are fully loaded and token check has completed
    ready: false,
    // short lived access token (see README in common/google)
    token: null as GoogleToken | null,
  }),

  getters: {
    // google is fully loaded and we have a live token
    googleAvailable: (state) => state.ready && !!state.token?.token,
    // the grant works for the app but lacks a scope Connected Apps need: an
    // older grant, from before CONNECTED_APP_SCOPES were requested, or a
    // stored token with no recorded scope
    grantIncomplete: (state) => {
      if (!state.token?.token) return false
      const granted = (state.token.scope ?? "").split(" ")
      return CONNECTED_APP_SCOPES.some((scope) => !granted.includes(scope))
    },
  },

  actions: {
    setAuthorizationNeeded(needed: boolean) {
      this.authorizationNeeded = needed
    },
    libraryLoadingComplete() {
      this.librariesLoaded = true
    },
    resetGoogle() {
      this.authorizationNeeded = true
      this.token = null
    },
    setReady(ready: boolean) {
      this.ready = ready
    },
    setToken(token: GoogleToken | null) {
      this.token = token
    },
  },
})
